/** design.md §P2 — Home launcher (Shell A). The assistant is the panel on the right. */

import { useQuery } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { ADMIN_NAV, appIcon, HomeShell, openAppMenu, useApps } from '@/components/Shell'
import { Button } from '@/components/ui/button'
import { Badge, Card, Skeleton } from '@/components/ui/surface'
import { ErrorState } from '@/components/states'
import { api } from '@/lib/api'
import { useAppTables } from '@/lib/meta'
import type { AppSummary } from '@/lib/types'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/auth'

function greeting() {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/**
 * design.md §P2: a tile shows ONE live number, not a dashboard. If the count is
 * slow or fails it renders an em dash rather than blocking the grid.
 */
function useTileCount(app: AppSummary) {
  const tables = useAppTables(app.status === 'active' ? app.slug : '')
  const first = tables.data?.[0]
  return useQuery({
    queryKey: ['tile-count', app.slug, first?.key],
    queryFn: () =>
      api.get<{ count: number }>(`/api/data/${app.slug}/${first!.slug}/_stats/count`),
    enabled: Boolean(first),
    retry: false,
    staleTime: 60_000,
  })
}

function AppTile({ app }: { app: AppSummary }) {
  const Icon = appIcon(app.icon)
  const ready = app.status === 'active'
  const tables = useAppTables(ready ? app.slug : '')
  const count = useTileCount(app)
  const first = tables.data?.[0]

  const body = (
    <>
      <span
        className={cn(
          'flex size-9 items-center justify-center rounded-lg transition-colors',
          ready ? 'bg-accent-soft text-accent' : 'bg-surface-2 text-faint',
        )}
      >
        <Icon className="size-[18px]" />
      </span>
      <span className="mt-3 block text-sm font-medium">{app.name}</span>
      <span className="mt-0.5 block text-xs text-muted">
        {!ready
          ? 'Not built yet'
          : count.data && first
            ? `${count.data.count.toLocaleString('en-IN')} ${first.label_plural.toLowerCase()}`
            : '—'}
      </span>
    </>
  )

  if (!ready) {
    return (
      <div
        className="relative rounded-lg border border-dashed border-border bg-surface/50 p-4 opacity-70"
        aria-disabled
      >
        <Badge tone="outline" className="absolute top-3 right-3 font-normal">
          Soon
        </Badge>
        {body}
      </div>
    )
  }

  return (
    <Link
      to={`/app/${app.slug}`}
      // design.md §3: an app opens with its sidebar in view.
      onClick={() => openAppMenu()}
      className="group rounded-lg border border-border bg-surface p-4 transition-colors hover:border-accent/40 hover:bg-surface-2/50"
    >
      {body}
    </Link>
  )
}

/**
 * design.md §P1: a signed-in user with no roles is a common day-one state and
 * must not look broken.
 */
function NoAccessYet() {
  return (
    <Card className="mt-6 max-w-xl border-warn/30 bg-warn-soft/40">
      <div className="flex gap-3 p-4">
        <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warn" />
        <div>
          <p className="text-sm font-medium">Your account has no access yet</p>
          <p className="mt-1 text-xs text-muted">
            You're signed in, but an administrator still needs to give you a level in
            an app before any apps appear here.
          </p>
        </div>
      </div>
    </Card>
  )
}

export function HomePage() {
  const { me, can } = useAuth()
  const apps = useApps()
  const navigate = useNavigate()
  // The same screens, in the same order, as the Administration sidebar: only the
  // ones this person may open (design.md §P9).
  const adminScreens = ADMIN_NAV.filter((item) => can(item.permission))

  const hasNoRoles =
    me !== null &&
    Object.keys(me.levels).length === 0 &&
    !me.platform_admin &&
    !me.is_superuser

  return (
    <HomeShell>
      <div className="mx-auto max-w-5xl px-6 py-10">
        <h1 className="text-xl font-semibold tracking-tight">
          {greeting()}, {me?.user.name?.split(' ')[0] ?? 'there'}
        </h1>

        {hasNoRoles ? <NoAccessYet /> : null}

        {apps.isError ? (
          <ErrorState error={apps.error} onRetry={() => void apps.refetch()} />
        ) : (
          <div className="mt-6 grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-3 xl:grid-cols-5">
            {apps.isLoading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} className="h-[7.25rem] rounded-lg" />
                ))
              : (apps.data ?? []).map((app) => <AppTile key={app.id} app={app} />)}
          </div>
        )}

        {adminScreens.length > 0 ? (
          <section className="mt-10">
            <h2 className="text-[11px] font-semibold tracking-wider text-faint uppercase">
              Administration
            </h2>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {adminScreens.map((item) => (
                <Button
                  key={item.to}
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(item.to)}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </HomeShell>
  )
}
